import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Card } from "../ui/Card.js";
import { ButtonLink } from "../ui/Button.js";
import { CheckIcon } from "../ui/icons.js";
import { getSkipNextSteps, setSkipNextSteps } from "../rfqs/submitPreference.js";

/**
 * Post-submit interstitial shown after a customer creates a new RFQ.
 *
 * Reassures the user that the request is in motion and explains what to
 * expect next. The user can opt out of seeing this screen for future
 * submissions; the preference is stored per-device in localStorage.
 *
 * NewRfq routes here on success unless the opt-out preference is set.
 */
export function RfqSubmitted() {
  const { id } = useParams<{ id: string }>();
  const [skip, setSkipState] = useState<boolean>(() => getSkipNextSteps());

  if (!id) return <Navigate to="/app/rfqs" replace />;

  function onToggleSkip(checked: boolean) {
    setSkipState(checked);
    setSkipNextSteps(checked);
  }

  return (
    <div className="mx-auto max-w-md p-4">
      <Card className="p-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-700">
          <CheckIcon className="h-8 w-8" aria-hidden />
        </div>
        <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">
          You did your part — we&rsquo;ll take it from here
        </h1>
        <ul className="mt-5 space-y-3 text-left text-sm text-slate-700">
          <li className="flex gap-3">
            <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-700" aria-hidden />
            <span>Your request was created.</span>
          </li>
          <li className="flex gap-3">
            <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-700" aria-hidden />
            <span>Contractors are being selected and notified.</span>
          </li>
          <li className="flex gap-3">
            <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-700" aria-hidden />
            <span>
              <span className="font-semibold text-slate-900">What to expect next:</span> a trusted
              professional will reach out to you soon.
            </span>
          </li>
        </ul>

        <div className="mt-6 flex flex-col gap-2">
          <ButtonLink to={`/app/rfqs/${id}`} variant="primary" fullWidth>
            View my request
          </ButtonLink>
          <ButtonLink to="/app/rfqs" variant="ghost" fullWidth>
            Back to requests
          </ButtonLink>
        </div>

        <label className="mt-6 flex items-start gap-3 text-left text-sm text-slate-600">
          <input
            type="checkbox"
            checked={skip}
            onChange={(e) => onToggleSkip(e.target.checked)}
            className="mt-0.5 h-5 w-5 flex-none rounded border-slate-300 text-brand-700 focus:ring-brand-600"
          />
          <span>Don&rsquo;t show this screen after I submit a request.</span>
        </label>
      </Card>
    </div>
  );
}
